import { useState, useCallback } from 'react';
import { type GuideFormData } from '../components/GuideForm';
import { 
  type GuideContentRequest,
  type VehicleFilterRequest,
  type EngineType 
} from '@/features/guides/api/generated';
import { type VehicleSelectionValue } from '@/shared/components/VehicleEnrichment';
import { getDefaultSourceLocale } from '@/shared/config/localeConfig';

interface UseGuideFormOptions {
  initialData?: Partial<GuideFormData>;
  defaultSourceLocale?: string;
}

export const useGuideForm = (options: UseGuideFormOptions = {}) => {
  const { 
    initialData = {}, 
    defaultSourceLocale = getDefaultSourceLocale() 
  } = options;

  const [formState, setFormState] = useState<GuideFormData>({
    content: {
      problem: '',
      possibleCause: '',
      solutions: [''],
      ...initialData.content
    },
    rootVehicle: initialData.rootVehicle || {},
    filters: initialData.filters || [],
    sourceLocale: initialData.sourceLocale || defaultSourceLocale
  });

  const updateContentField = useCallback((key: keyof GuideContentRequest, value: unknown) => {
    setFormState(prev => ({ 
      ...prev, 
      content: { ...prev.content, [key]: value } 
    }));
  }, []);

  const updateRootVehicleWithSelection = useCallback((selection: VehicleSelectionValue) => {
    setFormState(prev => ({
      ...prev,
      rootVehicle: {
        ...prev.rootVehicle,
        engineBaseType: selection.engineBaseType as EngineType | undefined,
        euroCode: selection.euroCode ?? undefined,
        manufacturer: selection.manufacturer,
        modelSeries: selection.modelSeries,
        modelYear: selection.modelYear,
      }
    }));
  }, []);

  const addSolution = useCallback(() => {
    setFormState(prev => ({ 
      ...prev, 
      content: { 
        ...prev.content, 
        solutions: [...(prev.content.solutions || []), ''] 
      } 
    }));
  }, []);

  const updateSolution = useCallback((index: number, value: string) => {
    setFormState(prev => {
      const next = [...(prev.content.solutions || [])];
      next[index] = value;
      return { 
        ...prev, 
        content: { 
          ...prev.content, 
          solutions: next 
        } 
      };
    });
  }, []);

  const removeSolution = useCallback((index: number) => {
    setFormState(prev => {
      const next = [...(prev.content.solutions || [])];
      next.splice(index, 1);
      return { 
        ...prev, 
        content: { 
          ...prev.content, 
          solutions: next 
        } 
      };
    });
  }, []);

  const reorderSolutions = useCallback((fromIndex: number, toIndex: number) => {
    setFormState(prev => {
      const list = [...(prev.content.solutions || [])];
      const [moved] = list.splice(fromIndex, 1);
      const adjustedIndex = fromIndex < toIndex ? toIndex - 1 : toIndex;
      list.splice(adjustedIndex, 0, moved);
      return { 
        ...prev, 
        content: { 
          ...prev.content, 
          solutions: list 
        } 
      };
    });
  }, []);

  const addFilter = useCallback((newFilter: VehicleFilterRequest) => {
    setFormState(prev => ({ 
      ...prev, 
      filters: [...prev.filters, newFilter] 
    }));
  }, []);

  const removeFilterAt = useCallback((index: number) => {
    setFormState(prev => {
      const next = [...prev.filters];
      next.splice(index, 1);
      return { ...prev, filters: next };
    });
  }, []);

  const updateFilterAt = useCallback((index: number, updatedFilter: VehicleFilterRequest) => {
    setFormState(prev => {
      const next = [...prev.filters];
      next[index] = updatedFilter;
      return { ...prev, filters: next };
    });
  }, []);

  const resetForm = useCallback((newData?: Partial<GuideFormData>) => {
    setFormState({
      content: {
        problem: '',
        possibleCause: '',
        solutions: [''],
        ...newData?.content
      },
      rootVehicle: newData?.rootVehicle || {},
      filters: newData?.filters || [],
      sourceLocale: newData?.sourceLocale || defaultSourceLocale
    });
  }, [defaultSourceLocale]);

  const isDirty = formState.content.problem.trim() !== '' || 
                  formState.content.possibleCause?.trim() !== '' ||
                  formState.content.solutions?.some(s => s.trim() !== '') ||
                  Object.keys(formState.rootVehicle).length > 0 ||
                  formState.filters.length > 0;

  return {
    formState,
    isDirty,
    updateContentField,
    updateRootVehicleWithSelection,
    addSolution,
    updateSolution,
    removeSolution,
    reorderSolutions,
    addFilter,
    removeFilterAt,
    updateFilterAt,
    resetForm,
    setFormState,
  };
};